import { useState, useContext, useEffect } from 'react';
import { useNextgenRouter, QuantityStepper, QuantityStepperSize, ButtonSize } from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import {
  CartEventsEnum,
  type Cart,
  CartConstantsEnum,
  type CartEntry,
  type InitialCartState,
  updateCart,
  PublishEvent,
  getActiveCart,
  type CartEntryDetails,
  InitialCartListState,
  ListEntry,
  ListResponse,
  publishLoaderEvent,
  myListApi,
  getMaxProductQty,
  cartApi,
} from '@commerce-cart-webcomponents/sdk';
import { useDispatch, useSelector } from 'react-redux';
import { AllCustomListContext, useExceptionToast, type CustomListContextType } from '@commerce-cart-webcomponents/ui';

import { ButtonLoader } from '../../../ui/common/components/cart-loader/button-loader/ButtonLoader';
import { QtySelectorPropTypes } from './types/propsTypes';
import styles from './QtySelector.module.scss';

export const QtySelector = ({
  disabled = false,
  entryObj,
  updateQuantityCustomHandler,
  disableApiCallOnQtyUpdate = false,
  min = '0',
}: QtySelectorPropTypes) => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useNextgenRouter();
  const cartId = router.params?.cartId ?? '';
  const listId = router.params?.listId ?? '';
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;
  const triggerToast = useExceptionToast();

  const activeCart = getActiveCart(cart, String(cartId));

  const [listIdWithoutAccountID] = (listId as String).split('#');
  const { customList, updateCustomList }: CustomListContextType = useContext(AllCustomListContext);
  const { myList }: InitialCartListState = useSelector((state: AppState) => state.commerceMyList);
  const maxProductCount = getMaxProductQty();

  const currentList = listIdWithoutAccountID === myList?.listId ? myList : customList;
  const cartEntry: CartEntryDetails | undefined = activeCart?.entries?.find(
    (entry: CartEntry) => entryObj.entryId == entry.entryId,
  );

  const listEntry: ListEntry | undefined = currentList?.payLoad?.entries?.find(
    (entry: ListEntry) => entryObj.entryId == entry.entryId,
  );

  const isList = listIdWithoutAccountID && listIdWithoutAccountID === currentList?.listId;
  const maxCount = cartEntry?.rationingResult?.[0]?.availableQuantity;
  const [count, setCount] = useState<number>(
    isList ? (listEntry?.quantity as number) : (cartEntry?.quantity as number),
  );
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    isList && setCount(listEntry?.quantity as number);
  }, [listEntry?.quantity]);

  const updateQuantity = (quantity: number, productRemoved?: boolean) => {
    if (disableApiCallOnQtyUpdate) {
      return 0;
    }
    if (!updateQuantityCustomHandler) {
      setShowLoader(true);
      const cartPayload = {
        entry: {
          entryId: entryObj.entryId,
          product: {
            id: cartEntry?.product.id as string,
          },
          quantity: quantity,
          bundleSlots: cartEntry?.bundleSlots?.map((slot) => ({
            ...slot,
            entries: slot.entries.map((entry) => ({
              ...entry,
              quantity: entry.quantity / cartEntry.quantity,
            })),
          })),
        },
        version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
      };

      const listPayload = {
        entry: {
          entryId: entryObj.entryId,
          bundleSlots: listEntry?.bundleSlots,
          product: {
            id: listEntry?.product.id as string,
          },
          quantity: quantity,
        },
        version: currentList?.version as number,
      };

      publishLoaderEvent(true, entryObj.entryId);
      isList
        ? myListApi
            .changeQtyForEntryFromList({
              entryId: entryObj.entryId,
              listId: currentList?.listId,
              payload: listPayload,
            })
            .then((updatedList: ListResponse) => {
              setCount(quantity);
              updateCustomList(updatedList);
            })
            .catch(() => {
              triggerToast();
            })
            .finally(() => {
              setShowLoader(false);
              publishLoaderEvent(false, entryObj.entryId);
            })
        : cartApi
            .changeQtyForEntryFromCart({
              cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
              subCartId: activeCart?.subCartId,
              entryId: entryObj.entryId,
              payload: cartPayload,
            })
            .then((res) => {
              setCount(quantity);
              setShowLoader(false);
              dispatch(updateCart(res as Cart));
              publishLoaderEvent(false, entryObj.entryId);
              clickSuccessHandler(res as Cart, productRemoved);
            })
            .catch(() => {
              triggerToast();
              setShowLoader(false);
              publishLoaderEvent(false, entryObj.entryId);
            });
    } else {
      updateQuantityCustomHandler(entryObj, quantity);
    }
    return 0;
  };

  const getProductEntry = () => activeCart?.entries?.find((entry) => entryObj.productId === entry.product.id);

  const clickSuccessHandler = (res: Cart, isRemoved = false) => {
    PublishEvent({
      cartEvent: isRemoved ? CartEventsEnum.RemoveCartEntry : CartEventsEnum.AddToCart,
      cartEntry: getProductEntry(),
      cartId: res.id,
      cart: res,
    });
  };

  return showLoader ? (
    <ButtonLoader className={styles['qty-selector-loader']} isStepper={true} size={ButtonSize.SMALL} />
  ) : (
    <QuantityStepper
      count={count}
      maxLength={String(maxProductCount).length}
      maxCount={maxProductCount}
      size={QuantityStepperSize.SMALL}
      minCount={+min}
      onBlur={() => {
        updateQuantity(count);
      }}
      inputChangeHandler={(qty: number) => {
        setCount(qty);
        return 0;
      }}
      plusClickHandler={(qty: number) => {
        if (maxCount && count > maxCount) {
          return 0;
        }
        updateQuantity(qty);
        return 0;
      }}
      minusClickHandler={(qty: number) => updateQuantity(qty, true)}
      disablePlusButton={maxCount && count > maxCount ? true : false}
      disabled={disabled}
    />
  );
};

export type { QtySelectorPropTypes };
